import { useCustomerIo } from './useCustomerIo';
import { analyticsPage } from '~/utils/acquisition';
import { trackEvent as trackPixelEvent } from '~/composables/useMetaPixel';
import { pixelEventForSegment } from '~/utils/pixelCore';
import { pushGtmEvent } from '~/composables/useGoogleTagManager';

// Define types for better type safety
interface Analytics {
   identify(userId: string, traits?: Record<string, any>): void;
   track(event: string, properties?: Record<string, any>): void;
   page(properties?: Record<string, any>): void;
   reset(): void;
   group(groupId: string, traits?: Record<string, any>): void;
   alias(userId: string, previousId?: string): void;
}

declare global {
   interface Window {
      analytics?: Analytics;
   }
}

export const useSegment = () => {
   const { identifyUser, resetUser, trackEvent, trackPageView } =
      useCustomerIo();

   const isAnalyticsAvailable = (): boolean => {
      return typeof window !== 'undefined' && !!window.analytics;
   };

   const identify = (userId: string, traits?: Record<string, any>) => {
      try {
         if (isAnalyticsAvailable()) {
            window.analytics?.identify(userId, traits);
         }
         identifyUser(traits);
      } catch (error) {
         console.error('Error in Segment identify:', error);
      }
   };

   const track = (event: string, properties?: Record<string, any>) => {
      // The Meta Pixel hears only the events listed in PIXEL_SEGMENT_EVENTS, by their
      // fixed pixel name. The properties are never passed on, and this does not wait
      // for Segment to be available. Moving a session is not a new booking.
      try {
         const pixelEvent = pixelEventForSegment(event);
         if (pixelEvent && !properties?.rescheduled) trackPixelEvent(pixelEvent);
      } catch {
         // The pixel must never break the app's own tracking.
      }
      try {
         pushGtmEvent(event, properties);
      } catch {
         // Nor must Google Tag Manager.
      }
      try {
         if (isAnalyticsAvailable()) {
            window.analytics?.track(event, properties);
         }
         trackEvent(event, properties);
      } catch (error) {
         console.error('Error in Segment track:', error);
      }
   };

   const page = (path = window.location.pathname) => {
      try {
         const properties = analyticsPage(new URL(path, window.location.origin), document.referrer);
         if (isAnalyticsAvailable()) {
            window.analytics?.page(properties);
         }
         trackPageView(properties.path);
      } catch (error) {
         console.error('Error in Segment page:', error);
      }
   };

   const reset = () => {
      try {
         if (isAnalyticsAvailable()) {
            window.analytics?.reset();
         }
         resetUser();
      } catch (error) {
         console.error('Error in Segment reset:', error);
      }
   };

   const group = (groupId: string, traits?: Record<string, any>) => {
      try {
         if (isAnalyticsAvailable()) {
            window.analytics?.group(groupId, traits);
         }
      } catch (error) {
         console.error('Error in Segment group:', error);
      }
   };

   const alias = (userId: string, previousId?: string) => {
      try {
         if (isAnalyticsAvailable()) {
            window.analytics?.alias(userId, previousId);
         }
      } catch (error) {
         console.error('Error in Segment alias:', error);
      }
   };

   return {
      identify,
      track,
      page,
      reset,
      group,
      alias,
   };
};
